// src/constants/roles.js
//
// CLIENT-SIDE MIRROR of functions/core/roles.js. Keep the canonical
// values, normalizers, and capability predicates in sync with the server
// module — they're copy-paste by design (separate package.json scopes,
// no shared build). Any new role value must land in both files.
//
// The resolver here uses the Firebase JS SDK instead of the Admin SDK,
// and reads platform claims from getIdTokenResult() rather than a
// server-side token object, but the returned ACCESS levels and the
// capability logic are identical to the server.

import { db } from '@/firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';

/* -------------------------------------------------------------------------- */
/*  Canonical role strings (what we WRITE)                                    */
/* -------------------------------------------------------------------------- */

export const TEAM_ROLES = Object.freeze({
  OWNER: 'owner',   // "Head Coach" in UI
  STAFF: 'staff',
});

export const CLUB_ROLES = Object.freeze({
  DIRECTOR:   'director',
  CLUB_ADMIN: 'club_admin',
});

// UI-friendly labels for role-picker dropdowns and badges.
export const TEAM_ROLE_LABELS = Object.freeze({
  [TEAM_ROLES.OWNER]: 'Head Coach',
  [TEAM_ROLES.STAFF]: 'Staff',
});

export const CLUB_ROLE_LABELS = Object.freeze({
  [CLUB_ROLES.DIRECTOR]:   'Director',
  [CLUB_ROLES.CLUB_ADMIN]: 'Club Admin',
});

/* -------------------------------------------------------------------------- */
/*  Legacy role strings (what we may READ from older docs)                    */
/* -------------------------------------------------------------------------- */

const LEGACY_TEAM_STAFF     = ['coach', 'manager', 'assistant', 'team_manager'];
const LEGACY_TEAM_OWNER     = ['head_coach'];
const LEGACY_CLUB_DIRECTOR  = ['owner'];
const LEGACY_CLUB_ADMIN     = ['admin'];

export function normalizeTeamRole(raw) {
  const r = String(raw || '').toLowerCase().trim();
  if (!r) return null;
  if (r === TEAM_ROLES.OWNER || LEGACY_TEAM_OWNER.includes(r)) return TEAM_ROLES.OWNER;
  if (r === TEAM_ROLES.STAFF || LEGACY_TEAM_STAFF.includes(r)) return TEAM_ROLES.STAFF;
  return null;
}

export function normalizeClubRole(raw) {
  const r = String(raw || '').toLowerCase().trim();
  if (!r) return null;
  if (r === CLUB_ROLES.DIRECTOR   || LEGACY_CLUB_DIRECTOR.includes(r)) return CLUB_ROLES.DIRECTOR;
  if (r === CLUB_ROLES.CLUB_ADMIN || LEGACY_CLUB_ADMIN.includes(r))    return CLUB_ROLES.CLUB_ADMIN;
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Access levels                                                             */
/* -------------------------------------------------------------------------- */

export const ACCESS = Object.freeze({
  NONE:              'none',
  TEAM_STAFF:        'team_staff',
  TEAM_OWNER:        'team_owner',
  CLUB_ADMIN:        'club_admin',
  CLUB_DIRECTOR:     'club_director',
  PLATFORM_SUPPORT:  'platform_support',
  PLATFORM_FOUNDER:  'platform_founder',
});

const ACCESS_ORDER = [
  ACCESS.NONE,
  ACCESS.TEAM_STAFF,
  ACCESS.TEAM_OWNER,
  ACCESS.CLUB_ADMIN,
  ACCESS.CLUB_DIRECTOR,
  ACCESS.PLATFORM_SUPPORT,
  ACCESS.PLATFORM_FOUNDER,
];

function maxAccess(a, b) {
  return ACCESS_ORDER.indexOf(a) >= ACCESS_ORDER.indexOf(b) ? a : b;
}

/* -------------------------------------------------------------------------- */
/*  Platform predicates                                                       */
/*                                                                            */
/*  `claims` is the object returned by getIdTokenResult().claims (or the     */
/*  token payload on the server). Callers that have a `firebaseUser` should  */
/*  do:                                                                      */
/*                                                                            */
/*    const { claims } = await firebaseUser.getIdTokenResult();              */
/*    if (isFounder(claims)) { ... }                                         */
/*                                                                            */
/*  See src/hooks/usePlatformClaims.js (to be added) for a React hook that   */
/*  caches this for components.                                              */
/* -------------------------------------------------------------------------- */

export function isFounder(claims)  { return !!(claims && claims.founder === true); }
export function isSupport(claims)  { return !!(claims && claims.support === true); }
export function isFinance(claims)  { return !!(claims && claims.finance === true); }
export function isPlatform(claims) { return isFounder(claims) || isSupport(claims); }

/* -------------------------------------------------------------------------- */
/*  Main resolver                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Compute the effective ACCESS level a user has on a specific team.
 * Mirrors functions/core/roles.js::resolveTeamAccess — see that file for
 * the full algorithm. Reads up to 3 Firestore docs; short-circuits on
 * platform claims.
 *
 * @param {object} p
 * @param {string} p.uid
 * @param {string} [p.teamId]
 * @param {string} [p.clubId]
 * @param {object} [p.claims]  JWT claims from getIdTokenResult().
 * @returns {Promise<string>}  One of ACCESS.*.
 */
export async function resolveTeamAccess({ uid, teamId = null, clubId = null, claims = {} } = {}) {
  if (!uid) return ACCESS.NONE;

  if (isFounder(claims)) return ACCESS.PLATFORM_FOUNDER;
  if (isSupport(claims)) return ACCESS.PLATFORM_SUPPORT;

  let best = ACCESS.NONE;
  let effectiveClubId = clubId;
  let isTeamOwner = false;

  if (teamId) {
    try {
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      if (teamSnap.exists()) {
        const t = teamSnap.data() || {};
        effectiveClubId = effectiveClubId || t.clubId || null;
        if (t.ownerId === uid || t.coachId === uid) isTeamOwner = true;
        if (Array.isArray(t.staffIds) && t.staffIds.includes(uid)) {
          best = maxAccess(best, ACCESS.TEAM_STAFF);
        }
      }
    } catch (_) { /* fall through */ }
  }

  if (teamId) {
    if (isTeamOwner) best = maxAccess(best, ACCESS.TEAM_OWNER);
    try {
      const memSnap = await getDoc(doc(db, 'teams', teamId, 'members', uid));
      if (memSnap.exists()) {
        const role = normalizeTeamRole(memSnap.data()?.role);
        if (role === TEAM_ROLES.OWNER)      best = maxAccess(best, ACCESS.TEAM_OWNER);
        else if (role === TEAM_ROLES.STAFF) best = maxAccess(best, ACCESS.TEAM_STAFF);
      }
    } catch (_) { /* ignore */ }
  }

  if (effectiveClubId) {
    try {
      const clubMemSnap = await getDoc(doc(db, 'clubs', effectiveClubId, 'members', uid));
      if (clubMemSnap.exists()) {
        const role = normalizeClubRole(clubMemSnap.data()?.role);
        if (role === CLUB_ROLES.DIRECTOR)        best = maxAccess(best, ACCESS.CLUB_DIRECTOR);
        else if (role === CLUB_ROLES.CLUB_ADMIN) best = maxAccess(best, ACCESS.CLUB_ADMIN);
      }
    } catch (_) { /* ignore */ }
  }

  return best;
}

/* -------------------------------------------------------------------------- */
/*  Sync "from-doc" resolver — no Firestore reads                             */
/* -------------------------------------------------------------------------- */

/**
 * Compute the best-known ACCESS level from an already-loaded team doc +
 * JWT claims, without touching Firestore. Use this on list pages (e.g.
 * Teams.jsx) where rendering N <useTeamAccess> hooks isn't viable.
 *
 * Limitation: this ignores the teams/{id}/members and clubs/{id}/members
 * subcollections. If a user has access ONLY via those — e.g. a Club
 * Director managing a team they're not on `staffIds` for — this helper
 * will return ACCESS.NONE for them. That's a safe default: they just
 * won't see management affordances on the list. When they open the team
 * page, useTeamAccess() there does the full async resolve and the UI
 * lights up correctly.
 *
 * In practice this is fine for Teams.jsx because its query is
 *   where('ownerId','==',uid) OR where('coachId','==',uid)
 *   OR where('staffIds','array-contains',uid)
 * so every team in the list already grants some direct access by the
 * team doc alone.
 *
 * @param {object} p
 * @param {string} p.uid
 * @param {object} [p.team]    The already-loaded team doc.
 * @param {object} [p.claims]  JWT claims from getIdTokenResult().
 * @returns {string}           One of ACCESS.*.
 */
export function teamAccessFromDoc({ uid, team = null, claims = {} } = {}) {
  if (!uid) return ACCESS.NONE;

  if (isFounder(claims)) return ACCESS.PLATFORM_FOUNDER;
  if (isSupport(claims)) return ACCESS.PLATFORM_SUPPORT;

  if (!team) return ACCESS.NONE;

  let best = ACCESS.NONE;

  // Team doc fields — ownerId/coachId are the canonical owner signals;
  // staffIds is the denormalized staff list the list query relies on.
  if (team.ownerId === uid || team.coachId === uid) {
    best = maxAccess(best, ACCESS.TEAM_OWNER);
  }
  if (Array.isArray(team.staffIds) && team.staffIds.includes(uid)) {
    best = maxAccess(best, ACCESS.TEAM_STAFF);
  }

  // Some denormalized shapes carry the viewer's role directly on the team
  // doc (team.role) or as a members map (team.members[uid].role). These
  // aren't canonical, but we honor them when present so legacy list
  // payloads don't regress.
  const inlineRole = normalizeTeamRole(
    team.role ?? team.members?.[uid]?.role
  );
  if (inlineRole === TEAM_ROLES.OWNER)      best = maxAccess(best, ACCESS.TEAM_OWNER);
  else if (inlineRole === TEAM_ROLES.STAFF) best = maxAccess(best, ACCESS.TEAM_STAFF);

  return best;
}

/* -------------------------------------------------------------------------- */
/*  Capability matrix  (mirrors functions/core/roles.js exactly)              */
/* -------------------------------------------------------------------------- */

export function atLeast(access, minimum) {
  return ACCESS_ORDER.indexOf(access) >= ACCESS_ORDER.indexOf(minimum);
}

export function canAccessTeam(access)            { return atLeast(access, ACCESS.TEAM_STAFF); }
export function canBroadcast(access)             { return atLeast(access, ACCESS.TEAM_STAFF); }
export function canRefundRegistration(access)    { return atLeast(access, ACCESS.TEAM_STAFF); }
export function canManageRoster(access)          { return atLeast(access, ACCESS.TEAM_STAFF); }
export function canRespondToWeather(access)      { return atLeast(access, ACCESS.TEAM_STAFF); }
export function canReadInboundReplies(access)    { return atLeast(access, ACCESS.TEAM_STAFF); }

export function canInviteStaff(access)           { return atLeast(access, ACCESS.TEAM_OWNER); }
export function canChangeStaffRole(access)       { return atLeast(access, ACCESS.TEAM_OWNER); }
export function canRemoveStaff(access)           { return atLeast(access, ACCESS.TEAM_OWNER); }

export function canDeleteTeam(access)            { return atLeast(access, ACCESS.TEAM_OWNER); }
export function canTransferTeamOwnership(access) { return atLeast(access, ACCESS.TEAM_OWNER); }

export function canManageClub(access)            { return atLeast(access, ACCESS.CLUB_ADMIN); }
export function canDeleteClub(access)            { return atLeast(access, ACCESS.CLUB_DIRECTOR); }
export function canAssignClubAdmins(access)      { return atLeast(access, ACCESS.CLUB_DIRECTOR); }

export function canGrantPlatformClaims(access)   { return access === ACCESS.PLATFORM_FOUNDER; }
export function canImpersonate(access)           { return atLeast(access, ACCESS.PLATFORM_SUPPORT); }
export function canViewAuditLog(access)          { return atLeast(access, ACCESS.PLATFORM_SUPPORT); }
